import { z } from "zod";

export const WIREGUARD_KEY_PATTERN = /^[A-Za-z0-9+/]{42}[AEIMQUYcgkosw048]=$/;

const UNSAFE_LABEL_CHARACTERS = /[\p{Cc}\p{Cf}<>"'`]/gu;

function sanitizedLabel(max: number) {
  return z
    .string()
    .transform((value) => value.replace(UNSAFE_LABEL_CHARACTERS, "").replace(/\s+/g, " ").trim())
    .pipe(z.string().min(1).max(max));
}

export const VpnEnrollRequestSchema = z.object({
  token: z.string().min(16).max(128),
  publicKey: z.string().regex(WIREGUARD_KEY_PATTERN, "not a WireGuard public key"),
  computerName: sanitizedLabel(64),
  windowsUser: sanitizedLabel(64),
});
export type VpnEnrollRequest = z.infer<typeof VpnEnrollRequestSchema>;

export const VpnEnrollResponseSchema = z.object({
  id: z.string().uuid(),
  pollKey: z.string(),
});
export type VpnEnrollResponse = z.infer<typeof VpnEnrollResponseSchema>;

export const VpnPollResponseSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("pending") }),
  z.object({ status: z.literal("rejected") }),
  z.object({
    status: z.literal("approved"),
    address: z.string(),
    hubPublicKey: z.string(),
    endpoint: z.string(),
    allowedIps: z.string(),
  }),
]);
export type VpnPollResponse = z.infer<typeof VpnPollResponseSchema>;

export const VpnPeerSchema = z.object({
  publicKey: z.string(),
  address: z.string(),
});
export const VpnPeersResponseSchema = z.object({ peers: z.array(VpnPeerSchema) });
export type VpnPeersResponse = z.infer<typeof VpnPeersResponseSchema>;

export const CreateVpnTokenRequestSchema = z.object({
  storeId: z.string().uuid(),
  autoApprove: z.boolean().default(false),
});
export type CreateVpnTokenRequest = z.input<typeof CreateVpnTokenRequestSchema>;

export const VpnTokenSchema = z.object({
  token: z.string(),
  expiresAt: z.string(),
  autoApprove: z.boolean(),
  store: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    url: z.string(),
  }),
});
export type VpnToken = z.infer<typeof VpnTokenSchema>;

export const VpnEnrollmentStatusSchema = z.enum(["pending", "approved", "rejected", "revoked"]);
export type VpnEnrollmentStatus = z.infer<typeof VpnEnrollmentStatusSchema>;

export const PlatformVpnEnrollmentSchema = z.object({
  id: z.string().uuid(),
  store: z.object({ id: z.string().uuid(), name: z.string().nullable() }),
  computerName: z.string(),
  windowsUser: z.string(),
  publicKey: z.string(),
  status: VpnEnrollmentStatusSchema,
  address: z.string().nullable(),
  requestedAt: z.string(),
  decidedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  autoApproved: z.boolean(),
});
export type PlatformVpnEnrollment = z.infer<typeof PlatformVpnEnrollmentSchema>;

export const VPN_STORE_URL_MAX = 2048;

export const MintVpnTokenRequestSchema = z.object({
  storeUrl: z.string().trim().min(1).max(VPN_STORE_URL_MAX),
  autoApprove: z.boolean().default(false),
});
export type MintVpnTokenRequest = z.input<typeof MintVpnTokenRequestSchema>;

export function vpnEnrollCommand(storeUrl: string): string {
  return `ansible-playbook shop-bundle.yml -e url=${storeUrl}`;
}
